// Shared participant comparator (OSPROD-82).
//
// Three surfaces render the same "users in a voice room" list and used
// to disagree on order — the call grid (VoiceChannelView) didn't sort
// at all and inherited LiveKit's join order, the space sidebar didn't
// sort either, and the channel list sorted by department + firstName
// but fell back to the raw `username` when firstName was missing,
// which made bracketed tags ([QA] / [PM] / [Android]) dominate the
// alphabet. Reconnects, mute toggles, and screen-share starts all
// reshuffled tiles in the grid because the underlying array changed
// shape; the sidebar list jumped on every voice-state event for the
// same reason.
//
// This util gives the three surfaces one comparator so the order is
// stable across reconnect, mute, screen-share, and voice activity. It
// strips the bracketed prefix the same way `displayInitial` does so
// "[QA] Aleksei" sorts under "A", and falls back to the userId for a
// total order — without the tie-break two participants with identical
// display names would otherwise rely on Array.prototype.sort's
// engine-defined stability and could swap places.
//
// Locale-aware compare with sensitivity:'base' covers Latin + Cyrillic
// and folds case/accents so "А" (Cyrillic) and "A" (Latin) sort
// together. numeric:true keeps "User 2" before "User 10".

const COLLATOR = new Intl.Collator(undefined, {
  sensitivity: 'base',
  numeric: true,
  ignorePunctuation: false,
})

// Strip leading "[TAG]" segments. Mirrors displayInitial — kept local
// instead of imported so the two utils stay independently swappable.
// "[QA] Aleksei I" → "Aleksei I"; "[Bot]" alone → "" so the caller
// falls back to the un-stripped name (sorting under "[").
function stripBracketPrefix(name: string): string {
  return name.replace(/\[[^\]]*\]/g, '').trim()
}

/** Display string used for sort. Prefers firstName, then prefix-stripped username, then raw username. */
export function participantSortKey(p: { user?: { firstName?: string | null; username?: string | null } | null }): string {
  const first = p.user?.firstName?.trim()
  if (first) return first
  const username = p.user?.username || ''
  const stripped = stripBracketPrefix(username)
  return stripped || username
}

type Sortable = {
  userId?: string | null
  user?: {
    firstName?: string | null
    username?: string | null
    department?: { name?: string | null } | null
  } | null
}

/**
 * Stable, locale-aware comparator for voice participants.
 *
 * Order: department ASC (empty / unassigned sinks to the bottom — admins
 * see all "no department" users grouped at the end instead of mingled
 * into the alphabet via undefined-vs-string compare), then firstName
 * (with [TAG] prefix stripped from the username fallback), then userId
 * for a total order so identical display names don't rely on V8 sort
 * stability.
 *
 * OSPROD-79 introduced the department-first grouping in ChannelList;
 * OSPROD-82 hoisted it here so VoiceChannelView (the call grid) and
 * SpaceSidebar share the exact same comparator. The three surfaces
 * agree on order across reconnect / mute / screen-share / voice
 * activity.
 */
export function compareParticipants(a: Sortable, b: Sortable): number {
  const aDept = a.user?.department?.name ?? ''
  const bDept = b.user?.department?.name ?? ''
  if (aDept !== bDept) {
    if (!aDept) return 1
    if (!bDept) return -1
    const dc = COLLATOR.compare(aDept, bDept)
    if (dc !== 0) return dc
  }
  const cmp = COLLATOR.compare(participantSortKey(a), participantSortKey(b))
  if (cmp !== 0) return cmp
  // Tie-break — userId is opaque but stable. Falls back to '' so two
  // participants without ids still compare consistently.
  return (a.userId || '').localeCompare(b.userId || '')
}
